import {
  Eye,
  EyeOff,
  Loader,
  Lock,
  Mail,
  Globe,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiFetch } from "../context/AuthContext";
import { clearProfileCache } from "../hooks/useProfile";
import { X, CheckCircle, AlertCircle } from "lucide-react";
//import mysteryBoxImage from "./christmas-tree-main/mystery-box.png";
import { sessionManager } from "../utils/sessionManager";
import { setClientIconUrl } from "../utils/clientIcon";
import Iconee from "../Images/photos/icon.png";
import BG from "../Images/sw.png";

// ===============================================
// TRANSLATION OBJECT - كائن الترجمة
// ===============================================
const translations = {
  en: {
    welcome: "Welcome",
    signInAccount: "Sign in to your account",
    emailAddress: "Email Address",
    enterEmail: "Enter your email",
    password: "Password",
    enterPassword: "Enter your password",
    rememberMe: "Remember Me",
    forgotPassword: "Forgot password?",
    signIn: "Sign In",
    processing: "Processing...",

    // Modal
    resetPassword: "Reset Password",
    resetPasswordSubtitle:
      "Enter your email address and we'll send you instructions to reset your password",
    sendResetLink: "Send Reset Link",
    sending: "Sending...",
    sentSuccessfully: "Sent Successfully",
    cancel: "Cancel",

    // Messages
    fillAllFields: "Please fill in all required fields",
    geolocationNotSupported: "Geolocation is not supported by your browser",
    loginNotAllowed: "Login not allowed outside the permitted area",
    firstLoginDetected:
      "First-time login detected. Please check your email for password reset instructions.",
    noPermission: "You do not have permission to access this system",
    loginSuccessful: "Login successful!",
    subscriptionNotFound:
      "Company subscription not found. Please contact support.",
    subscriptionInactive:
      "Company subscription is inactive. Please contact your administrator.",
    subscriptionExpired:
      "Company subscription has expired. Please renew to continue.",
    subscriptionValidationFailed: "Subscription validation failed.",
    invalidCredentials: "Invalid email or password",
    networkError: "Network error, please try again",
    unableToFetchLocation: "Unable to fetch location. Please allow access.",
    validEmailRequired: "Please enter a valid email address",
    resetInstructionsSent:
      "Password reset instructions have been sent to your email. Please check your inbox.",
    resetInstructionsFailed:
      "Failed to send reset instructions. Please try again.",
    networkErrorReset:
      "Network error. Please check your connection and try again.",
    loadingSplash: "Loading your experience...",
    redirectingSoon: "Redirecting you now...",
    splashAltText: "Client splash screen",
  },
  ar: {
    welcome: "مرحباً",
    signInAccount: "تسجيل الدخول إلى حسابك",
    emailAddress: "البريد الإلكتروني",
    enterEmail: "أدخل بريدك الإلكتروني",
    password: "كلمة المرور",
    enterPassword: "أدخل كلمة المرور",
    rememberMe: "تذكرني",
    forgotPassword: "نسيت كلمة المرور؟",
    signIn: "تسجيل الدخول",
    processing: "جاري المعالجة...",

    // Modal
    resetPassword: "إعادة تعيين كلمة المرور",
    resetPasswordSubtitle:
      "أدخل عنوان بريدك الإلكتروني وسنرسل لك تعليمات لإعادة تعيين كلمة المرور",
    sendResetLink: "إرسال رابط إعادة التعيين",
    sending: "جاري الإرسال...",
    sentSuccessfully: "تم الإرسال بنجاح",
    cancel: "إلغاء",

    // Messages
    fillAllFields: "يرجى ملء جميع الحقول المطلوبة",
    geolocationNotSupported: "الموقع الجغرافي غير مدعوم في متصفحك",
    loginNotAllowed: "تسجيل الدخول غير مسموح به خارج المنطقة المحددة",
    firstLoginDetected:
      "تم اكتشاف تسجيل دخول لأول مرة. يرجى التحقق من بريدك الإلكتروني للحصول على تعليمات إعادة تعيين كلمة المرور.",
    noPermission: "ليس لديك إذن للوصول إلى هذا النظام",
    loginSuccessful: "تم تسجيل الدخول بنجاح!",
    subscriptionNotFound:
      "لم يتم العثور على اشتراك الشركة. يرجى الاتصال بالدعم.",
    subscriptionInactive: "اشتراك الشركة غير نشط. يرجى الاتصال بالمسؤول.",
    subscriptionExpired: "انتهت صلاحية اشتراك الشركة. يرجى التجديد للمتابعة.",
    subscriptionValidationFailed: "فشل التحقق من الاشتراك.",
    invalidCredentials: "البريد الإلكتروني أو كلمة المرور غير صحيحة",
    networkError: "خطأ في الشبكة، يرجى المحاولة مرة أخرى",
    unableToFetchLocation: "تعذر الحصول على الموقع. يرجى السماح بالوصول.",
    validEmailRequired: "يرجى إدخال عنوان بريد إلكتروني صالح",
    resetInstructionsSent:
      "تم إرسال تعليمات إعادة تعيين كلمة المرور إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد.",
    resetInstructionsFailed:
      "فشل إرسال تعليمات إعادة التعيين. يرجى المحاولة مرة أخرى.",
    networkErrorReset:
      "خطأ في الشبكة. يرجى التحقق من اتصالك والمحاولة مرة أخرى.",
    loadingSplash: "جارٍ تحميل شاشتك المخصصة...",
    redirectingSoon: "سيتم تحويلك الآن...",
    splashAltText: "شاشة الترحيب الخاصة بالشركة",
  },
};

// Add at the top
const allowedArea = {
  lat: process.env.REACT_APP_OFFICE_LATITUDE,
  lng: process.env.REACT_APP_OFFICE_LONGITUDE,
  radius: 200,
};

function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// const ChristmasAnimation = ({ onAnimationComplete }) => {
//   const [showTransition, setShowTransition] = useState(false);

//   const handleBoxClick = () => {
//     setShowTransition(true);
//     setTimeout(() => {
//       window.location.href = "/christmas-tree-main/merry-christmas.html";
//     }, 500);
//   };

//   return (
//     <div
//       style={{
//         position: "fixed",
//         top: 0,
//         left: 0,
//         width: "100%",
//         height: "100vh",
//         backgroundColor: "#151522",
//         display: "flex",
//         alignItems: "center",
//         />
//         <img
//           src={mysteryBoxImage}
//           alt="Mystery Box"
//           style={{
//             width: "100%",
//             height: "100%",
//             animation: "mysteryBoxAnimation 3s infinite ease-in-out",
//           }}
//         />
//       </button>
//       <div
//         style={{
//           position: "absolute",
//           left: 0,
//           top: 0,
//           width: "100%",
//           height: "100vh",
//           backgroundColor: "#151522",
//           transition: "0.5s",
//           opacity: showTransition ? 1 : 0,
//           zIndex: showTransition ? 10 : -10,
//         }}
//       />
//       <style>{`
//         @keyframes mysteryBoxAnimation {
//           0%, 100% { transform: translateY(0); }
//           50% { transform: translateY(-50px); opacity: 0.3; }
//         }
//       `}</style>
//     </div>
//   );
// };

const ProfessionalLogin = () => {
  // ===============================================
  // STATE MANAGEMENT
  // ===============================================
  const [language, setLanguage] = useState("en"); // Language state
  const t = translations[language]; // Translation helper

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState(null);
  const [resetMessage, setResetMessage] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);
  //const [showChristmasAnimation, setShowChristmasAnimation] = useState(false);
  const [splashOverlay, setSplashOverlay] = useState({
    visible: false,
    url: null,
    loading: false,
  });

  const { login } = useAuth();
  const navigate = useNavigate();
  const splashTimerRef = useRef(null);
  const splashFailsafeTimerRef = useRef(null);

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
    isSmallScreen: window.innerHeight <= 600,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setScreenSize({
        width,
        height,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
        isSmallScreen: height <= 600,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (splashTimerRef.current) {
        clearTimeout(splashTimerRef.current);
      }
      if (splashFailsafeTimerRef.current) {
        clearTimeout(splashFailsafeTimerRef.current);
      }
    };
  }, []);

  const resolveSplashUrl = (record, baseUrl) => {
    if (!record) return null;
    const source = record.fullUrl || record.gifUrl;
    if (!source) return null;
    const trimmed = source.trim();
    if (!trimmed) return null;
    if (
      trimmed.toLowerCase() === "null" ||
      trimmed.toLowerCase() === "undefined"
    ) {
      return null;
    }
    if (/^https?:\/\//i.test(source)) {
      return source;
    }
    if (!baseUrl) {
      return source.startsWith("/") ? source : `/${source}`;
    }
    const normalizedBase = baseUrl.replace(/\/+$/, "");
    const normalizedPath = source.startsWith("/") ? source : `/${source}`;
    return `${normalizedBase}${normalizedPath}`;
  };

  const buildSplashUrlCandidates = (resolvedUrl, record, baseUrl) => {
    const candidates = [];
    const pushUnique = (value) => {
      if (!value) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      if (
        trimmed.toLowerCase() === "null" ||
        trimmed.toLowerCase() === "undefined"
      )
        return;
      if (!candidates.includes(trimmed)) {
        candidates.push(trimmed);
      }
    };

    pushUnique(resolvedUrl);

    if (record?.gifUrl) {
      const relativePath = record.gifUrl.startsWith("/")
        ? record.gifUrl
        : `/${record.gifUrl}`;
      if (baseUrl) {
        const normalizedBase = baseUrl.replace(/\/+$/, "");
        pushUnique(`${normalizedBase}${relativePath}`);
      }
      pushUnique(`${window.location.origin.replace(/\/$/, "")}${relativePath}`);
    }

    if (resolvedUrl) {
      try {
        const parsed = new URL(resolvedUrl, window.location.origin);
        const pageProtocol = window.location.protocol;
        if (parsed.protocol === "https:") {
          if (pageProtocol === "http:") {
            const httpVariant = new URL(parsed.toString());
            httpVariant.protocol = "http:";
            pushUnique(httpVariant.toString());

            const httpAlt = new URL(httpVariant.toString());
            if (!httpAlt.port && parsed.port) {
              httpAlt.port = parsed.port;
              pushUnique(httpAlt.toString());
            }

            if (httpAlt.port === "3000") {
              const httpPortVariant = new URL(httpAlt.toString());
              httpPortVariant.port = "3002";
              pushUnique(httpPortVariant.toString());
            }
          }
        }
      } catch (error) {
        console.debug("Unable to build HTTP fallback for splash URL", error);
      }
    }

    return candidates;
  };

  // Toggle language function
  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSplashOverlay({ visible: false, url: null, loading: false });
    if (splashTimerRef.current) {
      clearTimeout(splashTimerRef.current);
      splashTimerRef.current = null;
    }
    if (splashFailsafeTimerRef.current) {
      clearTimeout(splashFailsafeTimerRef.current);
      splashFailsafeTimerRef.current = null;
    }

    const normalizedEmail = email.toLowerCase().trim();

    if (!normalizedEmail || !password) {
      setError(t.fillAllFields);
      return;
    }

    // Check if there's already an active session
    const existingSession = sessionManager.getActiveSession();
    if (existingSession) {
      // User is already logged in another tab
      const userRole = existingSession.role || localStorage.getItem("role");

      const existingIcon =
        existingSession.clientIconUrl || localStorage.getItem("clientIconUrl");
      setClientIconUrl(existingIcon || null);

      // Navigate to appropriate dashboard
      let target;
      if (userRole === "admin") {
        localStorage.setItem("viewType", "user");
        target = "/userprofileadmin/professionalProfile";
      } else if (userRole === "user") {
        localStorage.setItem("viewType", "user");
        target = "/userprofile";
      } else if (userRole === "manager") {
        localStorage.setItem("viewType", "user");
        target = "/userprofilemanager/professionalProfile";
      } else if (userRole === "superadmin") {
        localStorage.setItem("viewType", "superadmin");
        target = "/DashboardSuper/ConfigurationManagement";
      }
      // Notify other components (e.g., sidebar) of viewType change
      window.dispatchEvent(new Event("managerViewTypeChanged"));

      setSuccess(t.loginSuccessful);
      login(existingSession.email || email, rememberMe, existingSession);
      //setShowChristmasAnimation(true);

      // Navigate to the target route
      if (splashTimerRef.current) {
        clearTimeout(splashTimerRef.current);
      }
      splashTimerRef.current = setTimeout(() => {
        navigate(target);
      }, 500);
      return;
    }

    if (!navigator.geolocation) {
      setError(t.geolocationNotSupported);
      return;
    }

    setIsLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        const BASE_URL = process.env.REACT_APP_BASE_URL;

        try {
          const res = await apiFetch(`${BASE_URL}/users/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: normalizedEmail,
              password,
            }),
          });

          const data = await res.json();

          if (res.ok) {
            if (data.firstLogin) {
              setError(data.message || t.firstLoginDetected);
              setIsLoading(false);
              return;
            }

            localStorage.setItem("token", data.token);

            // Clear any cached profile from a previous session
            clearProfileCache();

            // Fetch user profile from /users/me (independent of token payload)
            let profile = null;
            let role = null;
            let clientName = null;
            try {
              const profileRes = await apiFetch(`${BASE_URL}/users/me`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${data.token}`,
                },
              });
              if (profileRes.ok) {
                const profileJson = await profileRes.json();
                if (profileJson.success && profileJson.profile) {
                  profile = profileJson.profile;
                  Object.entries(profile).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                      localStorage.setItem(
                        key,
                        typeof value === 'object' ? JSON.stringify(value) : value
                      );
                    }
                  });
                  localStorage.setItem("email", email);
                  if (profile.role) {
                    role = profile.role;
                  }
                  if (profile.clientName) {
                    clientName = profile.clientName;
                  }
                }
              }
            } catch (profileErr) {
              console.warn("Failed to fetch user profile after login", profileErr);
            }

            setSuccess(t.loginSuccessful);

            let target;
            if (role === "admin") {
              localStorage.setItem("viewType", "user");
              target = "/userprofileadmin/professionalProfile";
            } else if (role === "user") {
              localStorage.setItem("viewType", "user");
              target = "/userprofile";
            } else if (role === "manager") {
              localStorage.setItem("viewType", "user");
              target = "/userprofilemanager/professionalProfile";
            } else if (role === "superadmin") {
              localStorage.setItem("viewType", "superadmin");
              target = "/DashboardSuper/ConfigurationManagement";
            } else {
              setError(t.noPermission);
              return;
            }
            window.dispatchEvent(new Event("managerViewTypeChanged"));

            // Splash logic (optional, fallback to old if needed)
            let splashRecord = data?.splashScreen || null;
            if ((!splashRecord || !splashRecord.gifUrl) && clientName) {
              try {
                const splashRes = await apiFetch(
                  `${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`,
                );
                if (splashRes.ok) {
                  const splashJson = await splashRes.json();
                  if (splashJson?.success && splashJson?.data) {
                    splashRecord = splashJson.data;
                  }
                }
              } catch (splashError) {
                console.warn(
                  "Failed to pull splash screen after login",
                  splashError,
                );
              }
            }

            const activeSplash =
              splashRecord && splashRecord.isActive !== false
                ? splashRecord
                : null;
            const clientIconUrl =
              activeSplash?.iconFullUrl || activeSplash?.iconUrl || null;

            setClientIconUrl(clientIconUrl);

            login(email, rememberMe, {
              email: profile?.employeeEmail || email,
              role: role,
              workingCompany: profile?.workingCompany,
              clientIconUrl,
              token: data.token,
              adminLevel: profile?.adminLevel || null,
            });
            const splashUrl = resolveSplashUrl(activeSplash, BASE_URL);

            if (splashUrl) {
              const candidateUrls = buildSplashUrlCandidates(
                splashUrl,
                activeSplash,
                BASE_URL,
              );
              if (candidateUrls.length > 0) {
                console.debug("Displaying splash GIF", {
                  candidates: candidateUrls,
                  client: clientName,
                });

                const splashImage = new window.Image();
                let attemptIndex = 0;

                const finalizeNavigation = (delay) => {
                  if (splashTimerRef.current) {
                    clearTimeout(splashTimerRef.current);
                  }
                  splashTimerRef.current = setTimeout(() => {
                    setSplashOverlay({
                      visible: false,
                      url: null,
                      loading: false,
                    });
                    navigate(target);
                    splashTimerRef.current = null;
                  }, delay);
                };

                const cleanupFailsafe = () => {
                  if (splashFailsafeTimerRef.current) {
                    clearTimeout(splashFailsafeTimerRef.current);
                    splashFailsafeTimerRef.current = null;
                  }
                };

                const startFailsafe = () => {
                  if (!splashFailsafeTimerRef.current) {
                    splashFailsafeTimerRef.current = setTimeout(() => {
                      setSplashOverlay((prev) =>
                        prev.visible ? { ...prev, loading: false } : prev,
                      );
                      navigate(target);
                      splashFailsafeTimerRef.current = null;
                    }, 7000);
                  }
                };

                const tryLoad = (index) => {
                  const nextUrl = candidateUrls[index];
                  if (!nextUrl) {
                    console.warn(
                      "No more splash GIF candidates available",
                      candidateUrls,
                    );
                    cleanupFailsafe();
                    finalizeNavigation(500);
                    return;
                  }
                  setSplashOverlay({
                    visible: true,
                    url: nextUrl,
                    loading: true,
                  });
                  splashImage.src = nextUrl;
                };

                splashImage.onload = () => {
                  cleanupFailsafe();
                  setSplashOverlay({
                    visible: true,
                    url: candidateUrls[attemptIndex],
                    loading: false,
                  });
                  finalizeNavigation(6000);
                };

                splashImage.onerror = () => {
                  console.warn(
                    "Splash GIF failed to load",
                    candidateUrls[attemptIndex],
                  );
                  attemptIndex += 1;
                  if (attemptIndex < candidateUrls.length) {
                    tryLoad(attemptIndex);
                    return;
                  }
                  cleanupFailsafe();
                  setSplashOverlay({ visible: true, url: null, loading: true });
                  finalizeNavigation(800);
                };

                startFailsafe();
                tryLoad(attemptIndex);
              } else {
                console.debug(
                  "Splash GIF skipped due to unresolved URL",
                  activeSplash,
                );
                setSplashOverlay({ visible: false, url: null, loading: false });
                splashTimerRef.current = setTimeout(() => {
                  navigate(target);
                  splashTimerRef.current = null;
                }, 500);
              }
            } else {
              if (activeSplash) {
                console.debug(
                  "Splash GIF skipped due to unresolved URL",
                  activeSplash,
                );
              } else {
                console.debug(
                  "No active splash GIF for client",
                  clientName,
                );
              }
              setSplashOverlay({ visible: false, url: null, loading: false });
              splashTimerRef.current = setTimeout(() => {
                navigate(target);
                splashTimerRef.current = null;
              }, 500);
              if (splashFailsafeTimerRef.current) {
                clearTimeout(splashFailsafeTimerRef.current);
                splashFailsafeTimerRef.current = null;
              }
            }
          } else {
            if (data.subscriptionError) {
              switch (data.errorType) {
                case "not_found":
                  setError(t.subscriptionNotFound);
                  break;
                case "inactive":
                  setError(t.subscriptionInactive);
                  break;
                case "expired":
                  setError(data.error || t.subscriptionExpired);
                  break;
                default:
                  setError(data.error || t.subscriptionValidationFailed);
              }
            } else {
              setError(data.error || t.invalidCredentials);
            }
          }
        } catch (err) {
          console.error("Login error:", err);
          setError(t.networkError);
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setError(t.unableToFetchLocation);
        setIsLoading(false);
      },
    );
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setIsResetModalOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    const normalizedResetEmail = resetEmail.toLowerCase().trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedResetEmail)) {
      setResetStatus("error");
      setResetMessage(t.validEmailRequired);
      return;
    }

    setIsResetLoading(true);
    setResetStatus(null);
    setResetMessage("");

    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await apiFetch(`${BASE_URL}/users/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedResetEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResetStatus("success");
        setResetMessage(t.resetInstructionsSent);

        if (process.env.NODE_ENV === "development" && data.resetToken) {
          console.log("🔑 Development Reset Token:", data.resetToken);
        }

        setTimeout(() => {
          handleCloseResetModal();
        }, 3000);
      } else {
        setResetStatus("error");
        setResetMessage(data.error || t.resetInstructionsFailed);
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setResetStatus("error");
      setResetMessage(t.networkErrorReset);
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleCloseResetModal = () => {
    setResetEmail("");
    setResetStatus(null);
    setResetMessage("");
    setIsResetLoading(false);
    setIsResetModalOpen(false);
  };

  const handleResetKeyPress = (e) => {
    if (e.key === "Enter" && !isResetLoading && resetStatus !== "success") {
      handleResetPasswordSubmit();
    }
  };

  const getResponsiveStyles = () => {
    const { isMobile, isTablet, isSmallScreen } = screenSize;
    const isRTL = language === "ar";

    return {
      // Language Toggle Button
      languageToggle: {
        position: "fixed",
        top: isMobile ? "16px" : "24px",
        right: isRTL ? "auto" : isMobile ? "16px" : "30px",
        left: isRTL ? (isMobile ? "16px" : "30px") : "auto",
        zIndex: 1000,
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        border: "2px solid #084d98",
        borderRadius: "12px",
        padding: isMobile ? "8px 16px" : "10px 20px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        color: "#084d98",
        fontSize: isMobile ? "14px" : "15px",
        fontWeight: 600,
        boxShadow: "0 4px 12px rgba(8, 77, 152, 0.2)",
        transition: "all 0.3s ease",
        fontFamily: "inherit",
      },

      splashOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(7, 11, 22, 0.94)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? "18px" : "24px",
        zIndex: 1200,
        color: "#ffffff",
        padding: isMobile ? "24px" : "32px",
        textAlign: "center",
        backdropFilter: "blur(6px)",
      },

      splashImage: {
        maxWidth: isMobile ? "80%" : "60%",
        maxHeight: isMobile ? "40vh" : "50vh",
        borderRadius: "16px",
        boxShadow: "0 20px 45px rgba(15, 23, 42, 0.35)",
        border: "1px solid rgba(255, 255, 255, 0.25)",
        objectFit: "contain",
      },

      splashMessage: {
        fontSize: isMobile ? "15px" : "18px",
        fontWeight: 600,
        letterSpacing: "0.3px",
        maxWidth: "420px",
        lineHeight: 1.5,
      },

      container: {
        margin: 0,
        padding: isMobile ? "16px" : isTablet ? "20px" : "0",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        boxSizing: "border-box",
        direction: isRTL ? "rtl" : "ltr",
      },

      modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: isMobile ? "16px" : "20px",
        animation: "fadeIn 0.3s ease",
      },

      modalContent: {
        background: "rgba(255, 255, 255, 0.98)",
        borderRadius: isMobile ? "20px" : "24px",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        width: "100%",
        maxWidth: isMobile ? "380px" : "460px",
        padding: isMobile ? "24px" : "32px",
        position: "relative",
        animation: "slideUp 0.3s ease",
        direction: isRTL ? "rtl" : "ltr",
      },

      modalCloseButton: {
        position: "absolute",
        top: isMobile ? "16px" : "20px",
        right: isRTL ? "auto" : isMobile ? "16px" : "20px",
        left: isRTL ? (isMobile ? "16px" : "20px") : "auto",
        background: "transparent",
        border: "none",
        color: "#6B7280",
        cursor: "pointer",
        padding: "8px",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s",
      },

      modalHeader: {
        marginBottom: isMobile ? "20px" : "24px",
        paddingRight: isRTL ? "0" : "40px",
        paddingLeft: isRTL ? "40px" : "0",
      },

      modalTitle: {
        fontSize: isMobile ? "22px" : "26px",
        fontWeight: "700",
        color: "#084d98",
        margin: "0 0 8px 0",
      },

      modalSubtitle: {
        fontSize: isMobile ? "14px" : "15px",
        color: "#6B7280",
        margin: 0,
        lineHeight: "1.5",
      },

      modalForm: {
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "16px" : "20px",
      },

      modalInputGroup: {
        position: "relative",
      },

      modalLabel: {
        display: "block",
        fontSize: isMobile ? "14px" : "15px",
        fontWeight: "600",
        color: "#374151",
        marginBottom: "8px",
      },

      modalInputWrapper: {
        position: "relative",
        display: "flex",
        alignItems: "center",
      },

      modalIcon: {
        position: "absolute",
        left: isRTL ? "auto" : "14px",
        right: isRTL ? "14px" : "auto",
        color: "#084d98",
        width: "20px",
        height: "20px",
        pointerEvents: "none",
      },

      modalInput: {
        width: "100%",
        padding: isMobile ? "12px 16px 12px 44px" : "14px 18px 14px 48px",
        paddingLeft: isRTL
          ? isMobile
            ? "16px"
            : "18px"
          : isMobile
            ? "44px"
            : "48px",
        paddingRight: isRTL
          ? isMobile
            ? "44px"
            : "48px"
          : isMobile
            ? "16px"
            : "18px",
        fontSize: isMobile ? "15px" : "16px",
        border: "2px solid #E5E7EB",
        borderRadius: "12px",
        outline: "none",
        transition: "all 0.3s",
        backgroundColor: "#F9FAFB",
        color: "#1F2937",
        fontFamily: "inherit",
      },

      modalStatusMessage: {
        padding: isMobile ? "12px" : "14px",
        borderRadius: "12px",
        fontSize: isMobile ? "14px" : "15px",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        lineHeight: "1.5",
      },

      modalSuccessMessage: {
        backgroundColor: "#ECFDF5",
        border: "1px solid #A7F3D0",
        color: "#065F46",
      },

      modalErrorMessage: {
        backgroundColor: "#FEF2F2",
        border: "1px solid #FECACA",
        color: "#991B1B",
      },

      modalButtonGroup: {
        display: "flex",
        gap: isMobile ? "10px" : "12px",
        marginTop: isMobile ? "8px" : "12px",
      },

      modalButton: {
        flex: 1,
        padding: isMobile ? "12px" : "14px",
        fontSize: isMobile ? "15px" : "16px",
        fontWeight: "600",
        borderRadius: "12px",
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        fontFamily: "inherit",
      },

      modalPrimaryButton: {
        backgroundColor: "#084d98",
        color: "white",
      },

      modalSecondaryButton: {
        backgroundColor: "#F3F4F6",
        color: "#374151",
      },

      backgroundImage: {
        position: "absolute",
        inset: "0",
        backgroundImage: `url(${BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: isMobile ? 0.7 : 1,
        pointerEvents: "none",
      },

      backgroundDecor: {
        position: "absolute",
        inset: "0",
        overflow: "hidden",
        pointerEvents: "none",
      },

      loginCard: {
        position: "relative",
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(20px)",
        borderRadius: isMobile ? "16px" : "24px",
        boxShadow: isMobile
          ? "0 15px 35px -8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)"
          : "0 25px 50px -12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        padding: isMobile ? "24px 20px" : isTablet ? "28px 24px" : "32px",
        width: "100%",
        maxWidth: isMobile ? "340px" : isTablet ? "380px" : "420px",
        maxHeight: isMobile && isSmallScreen ? "90vh" : "none",
        overflowY: isMobile && isSmallScreen ? "auto" : "visible",
        zIndex: 2,
        margin: isMobile ? "auto" : "0",
      },

      header: {
        textAlign: "center",
        marginBottom: isMobile ? "20px" : isTablet ? "24px" : "28px",
      },

      logo: {
        width: isMobile ? "100px" : isTablet ? "120px" : "140px",
        height: isMobile ? "100px" : isTablet ? "120px" : "140px",
        borderRadius: "50%",
        margin: isMobile ? "0 auto 12px" : "0 auto 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      },

      logoImg: {
        width: "110%",
        height: "250%",
        objectFit: "contain",
        transform: "translate(3px, 5px)",
      },

      title: {
        fontSize: isMobile ? "22px" : isTablet ? "25px" : "28px",
        fontWeight: 800,
        color: "#084d98ff",
        margin: 0,
        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
        lineHeight: 1.2,
      },

      subtitle: {
        color: "#084d98ff",
        fontSize: isMobile ? "13px" : "14px",
        marginTop: "6px",
        lineHeight: 1.4,
      },

      form: {
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "16px" : "20px",
      },

      fieldGroup: {
        position: "relative",
      },

      label: {
        display: "block",
        color: "#084d98ff",
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: 600,
        marginBottom: isMobile ? "6px" : "8px",
      },

      inputWrapper: {
        border: "1px solid #084d98ff",
        borderRadius: "16px",
        position: "relative",
      },

      inputIcon: {
        position: "absolute",
        left: isRTL ? "auto" : isMobile ? "10px" : "12px",
        right: isRTL ? (isMobile ? "10px" : "12px") : "auto",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#084d98ff",
        width: isMobile ? "18px" : "20px",
        height: isMobile ? "18px" : "20px",
        pointerEvents: "none",
        zIndex: 1,
      },

      input: {
        width: "100%",
        paddingLeft: isRTL
          ? isMobile
            ? "12px"
            : "14px"
          : isMobile
            ? "36px"
            : "44px",
        paddingRight: isRTL
          ? isMobile
            ? "36px"
            : "44px"
          : isMobile
            ? "12px"
            : "14px",
        paddingTop: isMobile ? "10px" : "12px",
        paddingBottom: isMobile ? "10px" : "12px",
        background: "rgba(255, 255, 255, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        borderRadius: isMobile ? "10px" : "12px",
        color: "#084d98ff",
        fontSize: isMobile ? "15px" : "16px",
        outline: "none",
        transition: "all 0.3s ease",
        boxSizing: "border-box",
        fontFamily: "inherit",
      },

      inputPassword: {
        paddingRight: isRTL
          ? isMobile
            ? "36px"
            : "44px"
          : isMobile
            ? "36px"
            : "44px",
        paddingLeft: isRTL
          ? isMobile
            ? "36px"
            : "44px"
          : isMobile
            ? "36px"
            : "44px",
      },

      showPasswordBtn: {
        position: "absolute",
        right: isRTL ? "auto" : isMobile ? "8px" : "10px",
        left: isRTL ? (isMobile ? "8px" : "10px") : "auto",
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        color: "#084d98ff",
        cursor: "pointer",
        padding: "4px",
        transition: "color 0.2s",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
      },

      rememberSection: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "-6px",
        flexWrap: isMobile ? "wrap" : "nowrap",
        gap: isMobile ? "8px" : "0",
      },

      checkboxLabel: {
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "6px" : "8px",
        cursor: "pointer",
        color: "#084d98ff",
        fontSize: isMobile ? "13px" : "14px",
        order: isMobile ? 2 : 1,
      },

      checkbox: {
        width: isMobile ? "16px" : "18px",
        height: isMobile ? "16px" : "18px",
        accentColor: "#084d98ff",
        cursor: "pointer",
        flexShrink: 0,
        outline: "2px solid #084d98ff",
        outlineOffset: "-2px",
      },

      forgotLink: {
        color: "#084d98ff",
        fontSize: isMobile ? "13px" : "14px",
        textDecoration: "none",
        transition: "color 0.2s",
        order: isMobile ? 1 : 2,
        whiteSpace: "nowrap",
      },

      error: {
        background: "rgba(254, 242, 242, 0.15)",
        border: "1px solid rgba(252, 165, 165, 0.3)",
        borderRadius: isMobile ? "10px" : "12px",
        padding: isMobile ? "10px" : "12px",
        color: "#ff6b6b",
        fontSize: isMobile ? "13px" : "14px",
        textAlign: "center",
        lineHeight: 1.4,
      },

      success: {
        background: "rgba(240, 253, 244, 0.15)",
        border: "1px solid rgba(167, 243, 208, 0.3)",
        borderRadius: isMobile ? "10px" : "12px",
        padding: isMobile ? "10px" : "12px",
        color: "#10b981",
        fontSize: isMobile ? "13px" : "14px",
        textAlign: "center",
        lineHeight: 1.4,
      },

      submitBtn: {
        width: "100%",
        background: "linear-gradient(135deg, #2e7cba, #084d98ff)",
        color: "white",
        padding: isMobile ? "10px 16px" : "12px 16px",
        borderRadius: isMobile ? "10px" : "12px",
        fontSize: isMobile ? "15px" : "16px",
        fontWeight: 700,
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        transform: "scale(1)",
        minHeight: isMobile ? "44px" : "48px",
        fontFamily: "inherit",
      },
    };
  };

  const styles = getResponsiveStyles();

  return (
    <>
      {/* {showChristmasAnimation && (
        <ChristmasAnimation
          onAnimationComplete={() => {
            setShowChristmasAnimation(false);
            navigate(navigationTarget);
          }}
        />
      )} */}

      {splashOverlay.visible && (
        <div style={styles.splashOverlay}>
          {splashOverlay.loading ? (
            <>
              <Loader
                size={screenSize.isMobile ? 40 : 48}
                className="animate-spin"
              />
              <p style={styles.splashMessage}>{t.loadingSplash}</p>
            </>
          ) : (
            <>
              {splashOverlay.url && (
                <img
                  src={splashOverlay.url}
                  alt={t.splashAltText}
                  style={styles.splashImage}
                />
              )}
              <p style={styles.splashMessage}>{t.redirectingSoon}</p>
            </>
          )}
        </div>
      )}

      <div style={styles.container}>
        {/* Language Toggle Button */}
        <button
          onClick={toggleLanguage}
          style={styles.languageToggle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#084d98";
            e.currentTarget.style.color = "white";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
            e.currentTarget.style.color = "#084d98";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Globe size={18} />
          {language === "en" ? "العربية" : "English"}
        </button>

        <div style={styles.backgroundImage} />
        <div style={styles.backgroundDecor}>
          <div style={styles.circle1} />
          <div style={styles.circle2} />
        </div>

        <div style={styles.loginCard}>
          <header style={styles.header}>
            <div style={styles.logo}>
              <img src={Iconee} alt="Infinity Logo" style={styles.logoImg} />
            </div>
            <h2 style={styles.title}>{t.welcome}</h2>
            <p style={styles.subtitle}>{t.signInAccount}</p>
          </header>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>{t.emailAddress}</label>
              <div style={styles.inputWrapper}>
                <Mail style={styles.inputIcon} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  onFocus={() => setIsEmailFocused(true)}
                  onBlur={() => setIsEmailFocused(false)}
                  style={{
                    ...styles.input,
                    boxShadow: isEmailFocused
                      ? "0 0 12px rgba(59, 130, 246, 0.8)"
                      : "none",
                    border: isEmailFocused
                      ? "1px solid rgba(59, 130, 246, 0.6)"
                      : styles.input.border,
                  }}
                  placeholder={t.enterEmail}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>{t.password}</label>
              <div style={styles.inputWrapper}>
                <Lock style={styles.inputIcon} />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  onFocus={() => setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  style={{
                    ...styles.input,
                    ...styles.inputPassword,
                    boxShadow: isPasswordFocused
                      ? "0 0 12px rgba(59, 130, 246, 0.8)"
                      : "none",
                    border: isPasswordFocused
                      ? "1px solid rgba(59, 130, 246, 0.6)"
                      : styles.input.border,
                  }}
                  placeholder={t.enterPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  style={styles.showPasswordBtn}
                  onMouseEnter={(e) => (e.target.style.color = "#084d98ff")}
                  onMouseLeave={(e) => (e.target.style.color = "#084d98ff")}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={screenSize.isMobile ? 18 : 20} />
                  ) : (
                    <Eye size={screenSize.isMobile ? 18 : 20} />
                  )}
                </button>
              </div>
            </div>

            <div style={styles.rememberSection}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={styles.checkbox}
                />
                {t.rememberMe}
              </label>
              <a
                href="#"
                style={styles.forgotLink}
                onMouseEnter={(e) => (e.target.style.color = "#084d98ff")}
                onMouseLeave={(e) => (e.target.style.color = "#084d98ff")}
                onClick={(e) => {
                  e.preventDefault();
                  handleChangePassword(e);
                }}
              >
                {t.forgotPassword}
              </a>
            </div>

            {error && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.success}>{success}</div>}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...styles.submitBtn,
                opacity: isLoading ? 0.8 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background =
                    "linear-gradient(135deg, #2e7cba, #2e7cba)";
                  e.target.style.transform = "scale(1.02)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background =
                    "linear-gradient(135deg, #2e7cba, #2e7cba)";
                  e.target.style.transform = "scale(1)";
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader
                    size={screenSize.isMobile ? 18 : 20}
                    className="animate-spin"
                  />
                  {t.processing}
                </>
              ) : (
                t.signIn
              )}
            </button>
          </form>
        </div>

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
            
            input::placeholder {
              color: #084d98ff;
              opacity: 0.6;
            }
            
            @media (max-width: 768px) {
              ::-webkit-scrollbar {
                width: 4px;
              }
              ::-webkit-scrollbar-track {
                background: rgba(255, 255, 255, 0.1);
                border-radius: 2px;
              }
              ::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.3);
                border-radius: 2px;
              }
              ::-webkit-scrollbar-thumb:hover {
                background: rgba(255, 255, 255, 0.5);
              }
            }
            
            button:focus-visible, input:focus-visible {
              outline: 2px solid rgba(59, 130, 246, 0.8);
              outline-offset: 2px;
            }
            
            * {
              -webkit-tap-highlight-color: transparent;
            }
            
            @media screen and (-webkit-min-device-pixel-ratio: 0) {
              select, textarea, input[type="text"], input[type="password"], 
              input[type="datetime"], input[type="datetime-local"], 
              input[type="date"], input[type="month"], input[type="time"], 
              input[type="week"], input[type="number"], input[type="email"], 
              input[type="url"], input[type="search"], input[type="tel"] {
                font-size: 16px !important;
              }
            }

            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              <span
                role="button"
                tabIndex={0}
                style={styles.forgotLink}
                onMouseEnter={(e) => (e.target.style.color = "#084d98ff")}
                onMouseLeave={(e) => (e.target.style.color = "#084d98ff")}
                onClick={handleChangePassword}
                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleChangePassword(e); }}
              >
                {t.forgotPassword}
              </span>
              pointer-events: none;
              z-index: 9999;
              overflow: hidden;
            }

            .snowflake {
              position: absolute;
              top: -20px;
              color: #fff;
              text-shadow: 
                0 0 10px rgba(255, 255, 255, 1),
                0 0 20px rgba(200, 230, 255, 0.8),
                0 0 30px rgba(150, 200, 255, 0.6);
              animation: fall linear infinite;
              user-select: none;
              filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.9));
            }

            @keyframes fall {
              <span
                role="button"
                tabIndex={0}
                style={styles.forgotLink}
                onMouseEnter={(e) => (e.target.style.color = "#084d98ff")}
                onMouseLeave={(e) => (e.target.style.color = "#084d98ff")}
                onClick={handleChangePassword}
                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleChangePassword(e); }}
              >
                {t.forgotPassword}
              </span>
              100% {
                transform: translateY(100vh) translateX(0) rotate(360deg);
              }
            }

            @media (max-width: 768px) {
              .snowflake {
                font-size: 12px !important;
              }
            }
          `}
        </style>

        {/* <div className="snowfall">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="snowflake"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${5 + Math.random() * 10}s`,
                opacity: Math.random() * 0.6 + 0.4,
                fontSize: `${12 + Math.random() * 12}px`,
              }}
            >
              {["❄", "❅", "❆", "✻", "✼", "❉"][Math.floor(Math.random() * 6)]}
            </div>
          ))}
        </div> */}

        {isResetModalOpen && (
          <div style={styles.modalOverlay} onClick={handleCloseResetModal}>
            <div
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                style={styles.modalCloseButton}
                onClick={handleCloseResetModal}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3F4F6";
                  e.currentTarget.style.color = "#374151";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#6B7280";
                }}
                aria-label="Close"
              >
                <X size={22} />
              </button>

              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{t.resetPassword}</h2>
                <p style={styles.modalSubtitle}>{t.resetPasswordSubtitle}</p>
              </div>

              <div style={styles.modalForm}>
                <div style={styles.modalInputGroup}>
                  <label style={styles.modalLabel}>{t.emailAddress}</label>
                  <div style={styles.modalInputWrapper}>
                    <Mail style={styles.modalIcon} />
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      onKeyPress={handleResetKeyPress}
                      placeholder={t.enterEmail}
                      disabled={isResetLoading || resetStatus === "success"}
                      style={{
                        ...styles.modalInput,
                        opacity:
                          isResetLoading || resetStatus === "success" ? 0.6 : 1,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#084d98";
                        e.target.style.backgroundColor = "#FFFFFF";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(8, 77, 152, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#E5E7EB";
                        e.target.style.backgroundColor = "#F9FAFB";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>

                {resetStatus && (
                  <div
                    style={{
                      ...styles.modalStatusMessage,
                      ...(resetStatus === "success"
                        ? styles.modalSuccessMessage
                        : styles.modalErrorMessage),
                    }}
                  >
                    {resetStatus === "success" ? (
                      <CheckCircle
                        size={20}
                        style={{ flexShrink: 0, marginTop: "2px" }}
                      />
                    ) : (
                      <AlertCircle
                        size={20}
                        style={{ flexShrink: 0, marginTop: "2px" }}
                      />
                    )}
                    <span>{resetMessage}</span>
                  </div>
                )}

                <div style={styles.modalButtonGroup}>
                  <button
                    type="button"
                    onClick={handleCloseResetModal}
                    disabled={isResetLoading}
                    style={{
                      ...styles.modalButton,
                      ...styles.modalSecondaryButton,
                      opacity: isResetLoading ? 0.5 : 1,
                      cursor: isResetLoading ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!isResetLoading) {
                        e.currentTarget.style.backgroundColor = "#E5E7EB";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isResetLoading) {
                        e.currentTarget.style.backgroundColor = "#F3F4F6";
                      }
                    }}
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleResetPasswordSubmit}
                    disabled={isResetLoading || resetStatus === "success"}
                    style={{
                      ...styles.modalButton,
                      ...styles.modalPrimaryButton,
                      opacity:
                        isResetLoading || resetStatus === "success" ? 0.7 : 1,
                      cursor:
                        isResetLoading || resetStatus === "success"
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!isResetLoading && resetStatus !== "success") {
                        e.currentTarget.style.backgroundColor = "#063a73";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(8, 77, 152, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isResetLoading && resetStatus !== "success") {
                        e.currentTarget.style.backgroundColor = "#084d98";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }
                    }}
                  >
                    {isResetLoading ? (
                      <>
                        <Loader size={18} className="animate-spin" />
                        {t.sending}
                      </>
                    ) : resetStatus === "success" ? (
                      <>
                        <CheckCircle size={18} />
                        {t.sentSuccessfully}
                      </>
                    ) : (
                      t.sendResetLink
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfessionalLogin;
